import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { useEffect, useMemo, useState } from 'react';
import { CircularProgress, TextField } from '@material-ui/core';
import {
  configApiRef,
  gitflicAuthApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  FieldExtensionComponentProps,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import {
  createFormField,
  FormFieldBlueprint,
} from '@backstage/plugin-scaffolder-react/alpha';
import {
  GITFLIC_READ_SCOPES,
  GITFLIC_WRITE_SCOPES,
} from '../apis/gitflicScopes';

const DEFAULT_SECRET_KEY = 'USER_OAUTH_TOKEN';

type GitflicOAuthFieldUiOptions = {
  secretKey?: string;
};

export const GitflicOAuthField = (
  props: FieldExtensionComponentProps<string, GitflicOAuthFieldUiOptions>,
) => {
  const { onChange, schema, uiSchema, required, rawErrors } = props;
  const gitflicAuthApi = useApi(gitflicAuthApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const { secrets, setSecrets } = useTemplateSecrets();
  const secretKey = uiSchema?.['ui:options']?.secretKey ?? DEFAULT_SECRET_KEY;
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  const helperText = useMemo(() => {
    if (error) {
      return error;
    }

    if (rawErrors?.length) {
      return rawErrors.join(', ');
    }

    return schema.description;
  }, [error, rawErrors, schema.description]);

  useEffect(() => {
    let cancelled = false;

    async function authorize() {
      setLoading(true);
      setError(undefined);

      try {
        const token =
          secrets[secretKey] ??
          (await gitflicAuthApi.getAccessToken([
            ...GITFLIC_READ_SCOPES,
            ...GITFLIC_WRITE_SCOPES,
          ]));

        if (!cancelled && !secrets[secretKey]) {
          setSecrets({ [secretKey]: token });
        }

        const [profile, identity] = await Promise.all([
          gitflicAuthApi.getProfile().catch(() => undefined),
          identityApi.getBackstageIdentity().catch(() => undefined),
        ]);

        const resolvedValue =
          profile?.displayName ||
          profile?.email ||
          identity?.userEntityRef ||
          configApi.getOptionalString('app.title') ||
          'Gitflic OAuth';

        if (!cancelled) {
          setValue(resolvedValue);
          onChange(resolvedValue);
        }
      } catch (e) {
        if (!cancelled) {
          const message =
            e instanceof Error
              ? e.message
              : 'Не удалось авторизоваться в Gitflic';
          setError(message);
          setValue('');
          onChange('');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void authorize();

    return () => {
      cancelled = true;
    };
  }, [
    configApi,
    gitflicAuthApi,
    identityApi,
    onChange,
    secretKey,
    secrets,
    setSecrets,
  ]);

  return (
    <TextField
      label={schema.title}
      value={loading ? 'Авторизация...' : value}
      required={required}
      disabled
      fullWidth
      error={Boolean(error || rawErrors?.length)}
      helperText={helperText}
      InputProps={{
        endAdornment: loading ? <CircularProgress size={16} /> : undefined,
      }}
    />
  );
};

export const GitflicOAuthFieldExtension = FormFieldBlueprint.make({
  name: 'gitflic-oauth-field',
  params: {
    field: async () =>
      createFormField({
        name: 'GitflicOAuthField',
        component: GitflicOAuthField,
      }),
  },
});

export const scaffolderGitflicFieldModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [GitflicOAuthFieldExtension],
});
